import type { SiteData } from '../../shared/types/site.js';
import { getServiceImageUrl, iconSvg } from '../constants.js';
import { getSector } from '../sector.js';
import { t } from '../i18n.js';
import { $, escapeHtml } from '../utils/index.js';

const TECH_PRINCIPLES = [
  'Clear focus',
  'Senior execution',
  'Steady delivery',
  'Long-term support',
];

const LABS_PRODUCT_SELECTION = [
  {
    title: 'A product starts with a real problem',
    body: 'We look for pain that is operationally real: something that wastes time, slows decisions, breaks coordination, or creates avoidable complexity for the people doing the work.',
  },
  {
    title: 'The target is an end-to-end platform',
    body: 'If the workflow can live in one coherent system, that matters. We prefer products that reduce switching between tools and bring the important work into one place.',
  },
  {
    title: 'AI is integrated when it creates leverage',
    body: 'We use AI where it improves analysis, automation, recommendations, search, or speed. It belongs inside the product when it makes the solution meaningfully stronger.',
  },
  {
    title: 'Resources are treated as a design constraint',
    body: 'Good products respect capital, engineering time, and attention. We care about systems that are efficient to build, efficient to run, and worth the resources they consume.',
  },
] as const;

const LABS_PRODUCT_OUTCOMES = [
  {
    title: 'Resolve the problem fully',
    body: 'A product should not just touch the problem. It should carry the user through the core job from start to finish with as little friction as possible.',
  },
  {
    title: 'Keep the workflow in one place',
    body: 'The best platform is the one that removes unnecessary switching. We want users to stay focused inside one system instead of stitching together five different tools.',
  },
  {
    title: 'Make decisions clearer',
    body: 'Good software should increase clarity. The user should understand what matters, what changed, and what to do next without fighting the interface.',
  },
  {
    title: 'Feel thoughtful, not bloated',
    body: 'We want products with backbone: capable, efficient, and strong enough to solve serious work without becoming heavy for the people using them.',
  },
] as const;

function renderTechServices(siteData: SiteData): string {
  const services = siteData.services ?? [];
  return `
    <section class="page-hero">
      <div class="container">
        <div class="services-hero-panel animate-on-scroll">
          <p class="page-hero-label">${t('services.hero.label')}</p>
          <h1 class="page-hero-title">${t('services.hero.title')}</h1>
          <p class="page-hero-intro">${t('services.hero.intro')}</p>
          <div class="services-hero-expectations" aria-label="What clients can expect">
            ${TECH_PRINCIPLES.map((item) => `<span>${item}</span>`).join('')}
          </div>
          <div class="page-hero-actions services-hero-actions">
            <a href="/contact-us" class="btn-primary">${t('services.cta.button')}</a>
          </div>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="container services-showcase">
        ${services
          .map((service, index) => {
            const imageUrl = getServiceImageUrl(service);
            return `
              <article class="service-card animate-on-scroll" style="animation-delay: ${(index % 2) * 0.05}s">
                <div class="service-card-visual" aria-hidden="true">
                  ${imageUrl ? `<img src="${imageUrl}" alt="" loading="lazy" />` : ''}
                  <div class="service-card-icon">${iconSvg[service.icon] ?? iconSvg.web}</div>
                </div>
                <div class="service-card-body">
                  <h3>${escapeHtml(service.title)}</h3>
                  <p>${escapeHtml(service.description)}</p>
                  <a href="/services/${encodeURIComponent(service.slug)}" class="text-link service-card-cta">${t('services.card.cta')}</a>
                </div>
              </article>
            `;
          })
          .join('')}
      </div>
    </section>
  `;
}

function renderLabsWalkthrough(): string {
  return `
    <section class="page-hero page-hero-split">
      <div class="container page-hero-grid">
        <div class="animate-on-scroll">
          <p class="page-hero-label">Walkthrough</p>
          <h1 class="page-hero-title">How Arkron Labs decides what deserves to be built.</h1>
          <p class="page-hero-intro">We choose product directions by one standard: they must solve a real problem deeply enough to justify existing as a serious platform. AI becomes part of that system when it creates real leverage. Efficiency matters too, because good products should respect both the user's time and the resources required to build them well.</p>
        </div>
        <div class="page-hero-aside labs-hero-aside animate-on-scroll">
          <p class="page-hero-aside-kicker">Selection logic</p>
          <div class="labs-hero-rail">
            <span>Problem first</span>
            <span>Platform thinking</span>
            <span>AI with purpose</span>
            <span>Efficient execution</span>
          </div>
        </div>
      </div>
    </section>
    <section class="section" id="current-progress" data-route-anchor="current-progress">
      <div class="container section-head animate-on-scroll">
        <p class="section-kicker">Product selection</p>
        <h2 class="section-title section-title-medium">What a product must prove before it gets our time.</h2>
        <p class="section-intro">These are the tests. If an idea cannot clear all of them, it is not strong enough. The point is not to ship more things. The point is to build the right thing with enough depth that it actually matters.</p>
      </div>
      <div class="container labs-flow-board" id="year-objectives" data-route-anchor="year-objectives">
        ${LABS_PRODUCT_SELECTION
          .map(
            (item, index) => `
              <article class="labs-flow-step animate-on-scroll" style="animation-delay: ${index * 0.05}s">
                <span class="labs-flow-index">0${index + 1}</span>
                <div class="labs-flow-node" aria-hidden="true"></div>
                <div class="labs-flow-card">
                  <h3>${item.title}</h3>
                  <p>${item.body}</p>
                </div>
              </article>
            `
          )
          .join('')}
      </div>
    </section>
    <section class="section" id="product-outcome" data-route-anchor="product-outcome">
      <div class="container section-head animate-on-scroll">
        <p class="section-kicker">Product outcome</p>
        <h2 class="section-title section-title-medium">What every Arkron product should feel like in practice.</h2>
        <p class="section-intro">The result should be concrete: less switching, clearer decisions, stronger flow, and a system that solves more of the job instead of leaving the user to finish it somewhere else.</p>
      </div>
      <div class="container labs-outcome-grid">
        ${LABS_PRODUCT_OUTCOMES
          .map(
            (item, index) => `
              <article class="labs-outcome-card animate-on-scroll" style="animation-delay: ${index * 0.05}s">
                <h3>${item.title}</h3>
                <p>${item.body}</p>
              </article>
            `
          )
          .join('')}
      </div>
    </section>
    <section class="section">
      <div class="container cta-panel animate-on-scroll cta-panel-compact">
        <div>
          <p class="section-kicker">Arkron Labs</p>
          <h2 class="section-title light section-title-medium">If this kind of product thinking feels right to you, start the conversation.</h2>
          <p class="section-intro light">We care about useful software, integrated systems, and products with enough weight to solve serious work properly. Reach out if you want to build with that level of intent.</p>
        </div>
        <div class="cta-panel-actions">
          <a href="/contact-us" class="btn-primary">Contact Us</a>
        </div>
      </div>
    </section>
  `;
}

export function renderServices(siteData: SiteData): void {
  const el = $('#page-services');
  if (!el) return;

  el.innerHTML = getSector() === 'labs' ? renderLabsWalkthrough() : renderTechServices(siteData);
}
